import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useConfirmService } from '../../../services/confirmService';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkingMode } from '../../../contexts/WorkingModeContext';
import apiService from '../../../services/api';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import SidePanel from '../../UI/SidePanel';
import ConfirmModal from '../../Notification/ConfirmModal';
import { Badge, BadgeFormData, Group, FilterState } from './types';
import { UserPermission } from '../../../types';
import BadgeList from './components/BadgeList';
import AddEditBadgePanel from './components/AddEditBadgePanel';
import styles from '../../../styles/BadgesAndAchievements.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import searchStyles from '../../../styles/SearchBar.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';
import FloatingWorkingMode from '../../UI/FloatingWorkingMode';

const BADGE_TYPES = [
  { value: 'points', label: 'Points Achievement' },
  { value: 'tasks', label: 'Task Completion' },
  { value: 'streak', label: 'Activity Streak' },
  { value: 'quality', label: 'Quality Performance' },
  { value: 'participation', label: 'Participation' },
  { value: 'special', label: 'Special Achievement' },
];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'global', label: 'Global' },
  { value: 'group', label: 'Group' },
];

const BadgesAndAchievements: React.FC = () => {
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const confirmService = useConfirmService();
  const { user } = useAuth();
  const { isWorkMode: contextWorkMode, setWorkMode } = useWorkingMode();

  const [badges, setBadges] = useState<Badge[]>([]);
  const [userBadges, setUserBadges] = useState<any[]>([]); // User's earned badges
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingBadges, setIsLoadingBadges] = useState(false);
  const [isLoadingUserBadges, setIsLoadingUserBadges] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [sidePanelMode, setSidePanelMode] = useState<'create' | 'edit'>('create');
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  // Permission states
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [hasEditPermissions, setHasEditPermissions] = useState(false);
  // Different filter states for work mode and personal mode
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    scope: 'all',
    group: '',
    badge_type: '',
    show_active_only: undefined // undefined = all, true = active only, false = inactive only
  });
  const [formData, setFormData] = useState<BadgeFormData>({
    name: '',
    description: '',
    badge_type: 'points',
    scope: 'group',
    group_id: '',
    image: '',
    sort_order: 0,
    is_active: true,
    is_visible: true,
    criteria_config: {
      points_threshold: 0,
      tasks_completed: 0,
      streak_days: 0,
      quality_threshold: 0,
      participation_threshold: 0,
    },
  });

  const badgeTypeOptions: DropdownOption[] = [
    { value: '', label: 'All Types' },
    ...BADGE_TYPES.map(type => ({ value: type.value, label: type.label }))
  ];

  const groupOptions: DropdownOption[] = [
    { value: '', label: 'All Groups' },
    ...groups.map(group => ({ value: group.id, label: group.name }))
  ];

  const shouldShowWorkingMode = hasEditPermissions || user?.role === 'admin';

  const isWorkMode = shouldShowWorkingMode ? contextWorkMode : false;

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          setUserPermissions(permissions);

          const editActions = ['add', 'edit', 'delete', 'create', 'update'];
          const hasEdit = permissions.some(
            perm => (perm.module === 'badges' || perm.module === 'achievements' || perm.module === 'badges_and_achievements') && editActions.includes(perm.action)
          );
          setHasEditPermissions(hasEdit);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        setUserPermissions([]);
        setHasEditPermissions(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  useEffect(() => {
    if (!shouldShowWorkingMode && contextWorkMode) {
      setWorkMode(false);
    }
  }, [shouldShowWorkingMode, contextWorkMode, setWorkMode]);

  useEffect(() => {
    loadGroups();
  }, []);

  // Load badges when filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isWorkMode) {
        loadBadges();
      } else {
        loadUserBadges();
      }
    }, filters.search ? 500 : 300); // Longer debounce for search

    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.scope, filters.group, filters.badge_type, filters.show_active_only, isWorkMode]);

  // Reset filters when switching modes
  useEffect(() => {
    // Reset badge_type and show_active_only when switching to personal mode
    if (!isWorkMode) {
      setFilters(prev => ({
        ...prev,
        badge_type: '',
        show_active_only: undefined // Reset to show all
      }));
    }
  }, [isWorkMode]);

  const loadGroups = async () => {
    try {
      const response = await apiService.getGroups();
      if (response.status === 'success') {
        setGroups(response.data);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  };

  const loadBadges = async () => {
    if (isLoadingBadges) return;

    setIsLoadingBadges(true);
    setLoading(true);
    showLoader('Loading badges...');

    try {
      const params: any = {};

      // Search - backend supported
      if (filters.search) {
        params.search = filters.search;
      }

      // Scope - backend supported
      if (filters.scope !== 'all') {
        params.scope = filters.scope;
      }

      // Group - backend supported
      if (filters.group) {
        params.group_id = filters.group;
      }

      // Badge type - backend supported (work mode only)
      if (filters.badge_type) {
        params.badge_type = filters.badge_type;
      }

      // Active status - backend supported (work mode only)
      // show_active_only is boolean: true = active only, false = inactive only, undefined = all
      if (filters.show_active_only === true) {
        params.is_active = true;
      } else if (filters.show_active_only === false) {
        params.is_active = false;
      }
      // If undefined, don't send the parameter (shows all)

      const response = await apiService.getBadges(params);
      if (response.status === 'success') {
        setBadges(response.data || []);
      } else {
        notificationService.error(response.message || 'Failed to load badges');
      }
    } catch (err: any) {
      console.error('Error loading badges:', err);
      notificationService.error(err.response?.data?.message || 'Failed to load badges');
    } finally {
      setLoading(false);
      setIsLoadingBadges(false);
      hideLoader();
    }
  };

  const loadUserBadges = async () => {
    if (isLoadingUserBadges || !user) return;

    setIsLoadingUserBadges(true);
    setLoading(true);
    showLoader('Loading your badges...');

    try {
      const params: any = {};

      // Search - backend supported
      if (filters.search) {
        params.search = filters.search;
      }

      // Scope - backend supported
      if (filters.scope !== 'all') {
        params.scope = filters.scope;
      }

      // Group - backend supported
      if (filters.group) {
        params.group_id = filters.group;
      }

      const response = await apiService.getCurrentUserBadges(params);
      if (response.status === 'success') {
        setUserBadges(response.data || []);
      } else {
        notificationService.error(response.message || 'Failed to load your badges');
      }
    } catch (err: any) {
      console.error('Error loading user badges:', err);
      notificationService.error(err.response?.data?.message || 'Failed to load your badges');
    } finally {
      setLoading(false);
      setIsLoadingUserBadges(false);
      hideLoader();
    }
  };

  const handleCreateBadge = () => {
    setSelectedBadge(null);
    setSidePanelMode('create');
    setFormData({
      name: '',
      description: '',
      badge_type: 'points',
      scope: 'group',
      group_id: '',
      image: '',
      sort_order: 0,
      is_active: true,
      is_visible: true,
      criteria_config: {
        points_threshold: 0,
        tasks_completed: 0,
        streak_days: 0,
        quality_threshold: 0,
        participation_threshold: 0,
      },
    });
    setIsSidePanelOpen(true);
  };

  const handleEditBadge = (badge: Badge) => {
    setSelectedBadge(badge);
    setSidePanelMode('edit');
    setFormData({
      name: badge.name,
      description: badge.description,
      badge_type: badge.badge_type,
      scope: badge.scope,
      group_id: badge.group?.id || '',
      image: badge.image_url || '',
      sort_order: badge.sort_order,
      is_active: badge.is_active,
      is_visible: badge.is_visible,
      criteria_config: badge.criteria_config,
    });
    setIsSidePanelOpen(true);
  };

  const handleImageUpload = (file: File) => {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      notificationService.error('Invalid file type. Please upload PNG, JPG, GIF, WebP, or SVG images.');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      notificationService.error('File size exceeds 10MB limit.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFormData(prev => ({
        ...prev,
        image: result
      }));
    };
    reader.onerror = () => {
      notificationService.error('Error reading file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (validTypes.includes(file.type)) {
        handleImageUpload(file);
      } else {
        notificationService.error('Invalid file type. Please upload PNG, JPG, GIF, WebP, or SVG images.');
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleSaveBadge = async () => {
    showLoader('Saving badge...');
    try {
      const badgeData = {
        ...formData,
        criteria_config: formData.criteria_config,
      };

      let response;
      if (sidePanelMode === 'create') {
        response = await apiService.createBadge(badgeData);
      } else {
        response = await apiService.updateBadge(selectedBadge!.id, badgeData);
      }

      if (response.status === 'success') {
        notificationService.success(
          `Badge ${sidePanelMode === 'create' ? 'created' : 'updated'} successfully`
        );
        setIsSidePanelOpen(false);
        if (isWorkMode) {
          loadBadges();
        } else {
          loadUserBadges();
        }
      } else {
        notificationService.error(response.message || 'Failed to save badge');
      }
    } catch (err: any) {
      console.error('Error saving badge:', err);
      notificationService.error(err.response?.data?.message || 'Failed to save badge');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteBadge = async (badge: Badge) => {
    const confirmed = await confirmService.show(
      'Delete Badge',
      `Are you sure you want to delete the badge "${badge.name}"? This action cannot be undone.`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    showLoader('Deleting badge...');
    try {
      const response = await apiService.deleteBadge(badge.id);
      if (response.status === 'success') {
        notificationService.success('Badge deleted successfully');
        if (isWorkMode) {
          loadBadges();
        } else {
          loadUserBadges();
        }
      } else {
        notificationService.error(response.message || 'Failed to delete badge');
      }
    } catch (err: any) {
      console.error('Error deleting badge:', err);
      notificationService.error(err.response?.data?.message || 'Failed to delete badge');
    } finally {
      hideLoader();
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof FilterState, value: string | boolean) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // In personal mode, show user badges; in work mode, show all badges
  // Backend handles filtering, so we just use the data directly
  const badgesToDisplay = isWorkMode ? badges : userBadges.map(ub => ub.badge);

  // No client-side filtering needed - backend handles all filtering including search
  const filteredBadges = badgesToDisplay;

  return (
    <div className={styles.badgesContainer}>
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmService.isOpen}
        title={confirmService.title}
        message={confirmService.message}
        okText={confirmService.options.okText}
        cancelText={confirmService.options.cancelText}
        onConfirm={confirmService.onConfirm}
        onCancel={confirmService.onCancel}
      />

      {/* Fixed Header */}
      <div className={styles.fixedHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.searchWrapper}>
              <div className={searchStyles.searchBar}>
                <input
                  type="text"
                  placeholder={isWorkMode ? "Search badges by name, description, or type" : "Search your badges"}
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <button type="button" onClick={() => isWorkMode ? loadBadges() : loadUserBadges()}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.headerActions}>
              {isWorkMode && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={handleCreateBadge}
                  title="Create Badge"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14"></path>
                  </svg>
                  <span className={styles.btnText}>Add</span>
                </button>
              )}
              <CustomDropdown
                options={SCOPE_OPTIONS}
                value={filters.scope}
                onChange={(value) => handleFilterChange('scope', Array.isArray(value) ? value[0] : value)}
                placeholder="All Scopes"
                size="sm"
                variant="outlined"
                className="filter"
              />
              <CustomDropdown
                options={groupOptions}
                value={filters.group}
                onChange={(value) => handleFilterChange('group', Array.isArray(value) ? value[0] : value)}
                placeholder="All Groups"
                size="sm"
                variant="outlined"
                className="filter"
              />
              {/* Badge Type filter - only in work mode */}
              {isWorkMode && (
                <CustomDropdown
                  options={badgeTypeOptions}
                  value={filters.badge_type}
                  onChange={(value) => handleFilterChange('badge_type', Array.isArray(value) ? value[0] : value)}
                  placeholder="All Types"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              )}
              {/* Active Status filter - only in work mode */}
              {isWorkMode && (
                <CustomDropdown
                  options={[
                    { value: 'true', label: 'Active Only' },
                    { value: 'false', label: 'Inactive Only' },
                    { value: '', label: 'All' }
                  ]}
                  value={filters.show_active_only === true ? 'true' : filters.show_active_only === false ? 'false' : ''}
                  onChange={(value) => {
                    const selectedValue = Array.isArray(value) ? value[0] : value;
                    if (selectedValue === 'true') {
                      handleFilterChange('show_active_only', true);
                    } else if (selectedValue === 'false') {
                      handleFilterChange('show_active_only', false);
                    } else {
                      handleFilterChange('show_active_only', undefined as any);
                    }
                  }}
                  placeholder="All Status"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              )}

              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => isWorkMode ? loadBadges() : loadUserBadges()}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                <span className={styles.btnText}>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.badgesSection}>
        {loading ? (
          <div className={styles.badgeList}>
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <div key={index} className={styles.badgeCardSkeleton}>
                <div className={styles.skeletonCardHeader}>
                  <div className={styles.skeletonCardHeaderLeft}>
                    <div className={styles.skeletonIconWrapper}></div>
                    <div className={styles.skeletonTitleSection}>
                      <div className={styles.skeletonTitle}></div>
                      <div className={styles.skeletonDescription}></div>
                    </div>
                  </div>
                  <div className={styles.skeletonCardActions}>
                    <div className={styles.skeletonActionBtn}></div>
                    <div className={styles.skeletonActionBtn}></div>
                  </div>
                </div>
                <div className={styles.skeletonCardBody}>
                  <div className={styles.skeletonTags}>
                    <div className={styles.skeletonTag}></div>
                    <div className={styles.skeletonTag}></div>
                    <div className={styles.skeletonTag}></div>
                  </div>
                  <div className={styles.skeletonMetadata}>
                    <div className={styles.skeletonMetadataItem}></div>
                    <div className={styles.skeletonMetadataItem}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <BadgeList
            badges={filteredBadges}
            onEdit={handleEditBadge}
            onDelete={handleDeleteBadge}
            onCreateBadge={handleCreateBadge}
            isWorkMode={isWorkMode}
            currentUserId={user?.id}
            userBadges={userBadges}
          />
        )}
      </div>

      {/* Side Panel */}
      <SidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        title={
          sidePanelMode === 'create' ? 'Create Badge' :
            'Edit Badge'
        }
      >
        <AddEditBadgePanel
          mode={sidePanelMode}
          badge={selectedBadge}
          formData={formData}
          groups={groups}
          isDragOver={isDragOver}
          onFormDataChange={setFormData}
          onSave={handleSaveBadge}
          onCancel={() => setIsSidePanelOpen(false)}
          onImageUpload={handleImageUpload}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        />
      </SidePanel>

      {/* Floating Working Mode Button - Show if user has permissions OR is admin OR is group admin */}
      {shouldShowWorkingMode && (
        <FloatingWorkingMode
          position="bottom-right"
          activeLabel="Work Mode"
          inactiveLabel="Personal"
        />
      )}
    </div>
  );
};

export default BadgesAndAchievements;

